import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Form, Button, message } from 'antd'
import dayjs from 'dayjs'
import { DatePickerF, SelectF, TagSelectF } from '@/i2antdV5'
import { fetchTuinaMemberList } from '@/redux/tuina/memberListSlice'
import { fetchTuinaMemberEntitlements } from '@/redux/tuina/memberEntitlementsSlice'
import { fetchTuinaTherapistOnDuty } from '@/redux/tuina/therapistOnDutySlice'
import { fetchTuinaReservationSlots } from '@/redux/tuina/reservationSlotsSlice'
import { TUINA_RECENT_ISSUE_OPTIONS } from '@/views/tuina/options'
import MemberModal from '@/views/tuina/members/share/MemberModal'
import PurchaseOrderModal from '@/views/tuina/projects/share/PurchaseOrderModal'
import i2Math from '@/utils/i2Math'
import './ReservationCreateForm.less'

const FormItem = Form.Item

function formatSlotMinutes (raw) {
  const n = Number(raw)
  if (!Number.isFinite(n)) return ''
  const hh = Math.floor(n / 60)
  const mm = n % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

class ReservationCreateForm extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    storeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    memberList: PropTypes.object,
    memberEntitlements: PropTypes.object,
    therapistOnDuty: PropTypes.object,
    reservationSlots: PropTypes.object,
    dispatch: PropTypes.func
  }

  componentDidMount () {
    this.fetchDeps()
    this.syncRecord()
  }

  componentDidUpdate (prevProps) {
    if (prevProps.storeId !== this.props.storeId) this.fetchDeps()
  }

  fetchDeps = () => {
    const { dispatch, storeId } = this.props
    const query = {}
    if (storeId != null && storeId !== '') query.storeId = storeId
    this.fetchMembers()
    dispatch(fetchTuinaTherapistOnDuty({ query }))
  }

  fetchMembers = (phone) => {
    const { dispatch, storeId } = this.props
    const query = { pageNumber: 1, pageSize: 200 }
    if (storeId != null && storeId !== '') query.storeId = storeId
    if (phone) query.phone = phone
    return dispatch(fetchTuinaMemberList({ query }))
  }

  handleMemberSearch = (input) => {
    if (this.memberSearchTimer) clearTimeout(this.memberSearchTimer)
    this.memberSearchTimer = setTimeout(() => {
      this.fetchMembers(String(input || '').trim())
    }, 300)
  }

  componentWillUnmount () {
    if (this.memberSearchTimer) clearTimeout(this.memberSearchTimer)
  }

  handleMemberCreated = (created) => {
    const { form } = this.props
    this.fetchMembers()
      .unwrap()
      .then((payload) => {
        const list = payload && payload.data && Array.isArray(payload.data.list)
          ? payload.data.list
          : []
        const createdId = created && typeof created === 'object' ? created.id : created
        const createdPhone = created && created.phone
        const hit = list.find((row) => {
          if (createdId != null && createdId !== '' && String(row.id) === String(createdId)) return true
          if (createdPhone && row.phone === createdPhone) return true
          return false
        })
        if (!hit || !form || !form.current) return
        form.current.setFieldsValue({
          memberId: hit.id,
          projectId: undefined,
          startTime: undefined
        })
        this.fetchEntitlements(hit.id)
      })
      .catch((err) => message.error(typeof err === 'string' ? err : '加载会员失败'))
  }

  syncRecord = () => {
    const { form, date } = this.props
    if (!form || !form.current) return
    const appointmentDate = date != null && date !== ''
      ? date
      : dayjs().startOf('day').valueOf()
    form.current.setFieldsValue({
      appointmentDate,
      memberId: undefined,
      projectId: undefined,
      therapistId: undefined,
      startTime: undefined,
      remark: []
    })
  }

  fetchEntitlements = (memberId) => {
    const { dispatch } = this.props
    if (memberId == null || memberId === '') return Promise.resolve()
    return dispatch(fetchTuinaMemberEntitlements({ query: { id: memberId } }))
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载可服务项目失败'))
  }

  handlePurchaseSuccess = (projectId) => {
    const { form } = this.props
    const memberId = form && form.current ? form.current.getFieldValue('memberId') : undefined
    this.fetchEntitlements(memberId).then(() => {
      if (projectId == null || projectId === '') return
      if (!form || !form.current) return
      form.current.setFieldsValue({ projectId, startTime: undefined })
      this.handleSlotDepsChange()
    })
  }

  handleSlotDepsChange = () => {
    const { form, dispatch } = this.props
    if (!form || !form.current) return
    form.current.setFieldsValue({ startTime: undefined })
    const { projectId, therapistId, appointmentDate } = form.current.getFieldsValue()
    if (!projectId || !therapistId || !appointmentDate) return
    dispatch(fetchTuinaReservationSlots({
      query: { projectId, therapistId, date: appointmentDate }
    }))
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载可约时间失败'))
  }

  handleValuesChange = (changed) => {
    if ('memberId' in changed) {
      const { form } = this.props
      if (form && form.current) {
        form.current.setFieldsValue({ projectId: undefined, startTime: undefined })
      }
      this.fetchEntitlements(changed.memberId)
    }
    if (
      !('projectId' in changed) &&
      !('therapistId' in changed) &&
      !('appointmentDate' in changed)
    ) return
    this.handleSlotDepsChange()
  }

  render () {
    const { form, memberList, memberEntitlements, therapistOnDuty, reservationSlots } = this.props
    const memberRows = Array.isArray(memberList.data.list) ? memberList.data.list : []
    const therapistRows = Array.isArray(therapistOnDuty.data) ? therapistOnDuty.data : []
    const memberOptions = memberRows
      .filter((row) => row.status !== 'FROZEN')
      .map((row) => ({
        id: row.id,
        value: row.phone ? `${row.name} ${row.phone}` : row.name,
        phone: row.phone
      }))
    const therapistOptions = therapistRows.map((row) => ({
      id: row.id,
      value: row.status === 'off_duty' ? `${row.name} 离` : row.name
    }))
    const memberId = form && form.current ? form.current.getFieldValue('memberId') : undefined
    const entitlementsMatch = String(memberEntitlements.query.id) === String(memberId)
    const entitlementRows = entitlementsMatch && Array.isArray(memberEntitlements.data)
      ? memberEntitlements.data
      : []
    const projectOptions = []
    entitlementRows.forEach((row) => {
      const n = Number(row.availableCount)
      if (!row.availableCount || !Number.isFinite(n) || n === 0) return
      const exist = projectOptions.find((item) => String(item.id) === String(row.projectId))
      if (exist) {
        exist.availableCount = i2Math.i2Add(exist.availableCount, n)
        exist.value = Number.isFinite(Number(row.durationMinutes))
          ? `${row.projectName}（${row.durationMinutes}分）剩${exist.availableCount}次`
          : `${row.projectName} 剩${exist.availableCount}次`
        return
      }
      projectOptions.push({
        id: row.projectId,
        availableCount: n,
        value: Number.isFinite(Number(row.durationMinutes))
          ? `${row.projectName}（${row.durationMinutes}分）剩${n}次`
          : `${row.projectName} 剩${n}次`
      })
    })
    let projectPlaceholder = '请先选择会员'
    if (memberId && memberEntitlements.fetching) projectPlaceholder = '正在查询可服务项目'
    if (memberId && !memberEntitlements.fetching && projectOptions.length) projectPlaceholder = '请选择项目'
    if (memberId && !memberEntitlements.fetching && !projectOptions.length) projectPlaceholder = '暂无可服务项目'

    return (
      <Form ref={form} layout="vertical" onValuesChange={this.handleValuesChange}>
        <FormItem label="会员" required>
          <div className="tuina-reservation-member-row">
            <span className="tuina-reservation-member-select">
              <FormItem
                name="memberId"
                noStyle
                rules={[{ required: true, message: '请选择会员' }]}
              >
                <SelectF
                  options={memberOptions}
                  modalProps={{
                    size: 'large',
                    placeholder: '请选择会员',
                    loading: memberList.fetching,
                    onSearch: this.handleMemberSearch,
                    style: { width: '100%' }
                  }}
                />
              </FormItem>
            </span>
            <MemberModal
              actType="create"
              storeId={this.props.storeId}
              onSuccess={this.handleMemberCreated}
            >
              <Button size="large">快速添加会员</Button>
            </MemberModal>
          </div>
        </FormItem>
        <FormItem label="项目" required extra="选会员后查询可服务项目与可约次数">
          <div className="tuina-reservation-member-row">
            <span className="tuina-reservation-member-select">
              <FormItem
                name="projectId"
                noStyle
                rules={[{ required: true, message: '请选择项目' }]}
              >
                <SelectF
                  options={projectOptions}
                  modalProps={{
                    size: 'large',
                    disabled: !memberId,
                    placeholder: projectPlaceholder,
                    loading: memberEntitlements.fetching,
                    notFoundContent: '暂无可服务项目',
                    style: { width: '100%' }
                  }}
                />
              </FormItem>
            </span>
            <PurchaseOrderModal
              storeId={this.props.storeId}
              memberId={memberId}
              zIndex={1100}
              disabledModal={!memberId}
              onSuccess={this.handlePurchaseSuccess}
            >
              <Button size="large" disabled={!memberId}>快速购买项目</Button>
            </PurchaseOrderModal>
          </div>
        </FormItem>
        <FormItem
          label="师傅"
          name="therapistId"
          rules={[{ required: true, message: '请选择师傅' }]}
        >
          <SelectF
            options={therapistOptions}
            modalProps={{
              size: 'large',
              placeholder: '请选择师傅',
              loading: therapistOnDuty.fetching,
              style: { width: '100%' }
            }}
          />
        </FormItem>
        <FormItem
          label="预约日期"
          name="appointmentDate"
          rules={[{ required: true, message: '请选择预约日期' }]}
        >
          <DatePickerF
            modalProps={{
              size: 'large',
              allowClear: false,
              format: 'YYYY-MM-DD',
              style: { width: '100%' },
              getPopupContainer: () => document.body,
              disabledDate: (current) => current && current.isBefore(dayjs(), 'day')
            }}
          />
        </FormItem>
        <FormItem
          noStyle
          shouldUpdate={(prev, cur) =>
            prev.projectId !== cur.projectId ||
            prev.therapistId !== cur.therapistId ||
            prev.appointmentDate !== cur.appointmentDate
          }
        >
          {() => {
            const projectId = form && form.current ? form.current.getFieldValue('projectId') : undefined
            const therapistId = form && form.current ? form.current.getFieldValue('therapistId') : undefined
            const appointmentDate = form && form.current ? form.current.getFieldValue('appointmentDate') : undefined
            const ready = !!(projectId && therapistId && appointmentDate)
            const slotsQuery = reservationSlots.query
            const slotsMatch =
              slotsQuery.projectId === projectId &&
              slotsQuery.therapistId === therapistId &&
              slotsQuery.date === appointmentDate
            const slotRows = slotsMatch && Array.isArray(reservationSlots.data)
              ? reservationSlots.data.filter((row) => {
                if (row.startMinute == null || row.startMinute === '') return false
                return true
              })
              : []
            const slotOptions = slotRows.map((row) => {
              const unavailable = row.available !== true
              const timeText = `${formatSlotMinutes(row.startMinute)} 至 ${formatSlotMinutes(row.endMinute)}`
              return {
                id: row.startMinute,
                value: unavailable ? `${timeText} 不可约` : timeText,
                selectDisabled: unavailable
              }
            })
            let placeholder = '请先选择项目和师傅'
            if (ready && reservationSlots.fetching) placeholder = '正在查询可约时间'
            if (ready && !reservationSlots.fetching && slotOptions.length) placeholder = '请选择时间'
            if (ready && !reservationSlots.fetching && !slotOptions.length) placeholder = '暂无可约时间'
            return (
              <FormItem
                label="时间"
                name="startTime"
                extra="选好项目和师傅后查询可约时间"
                rules={[{ required: true, message: '请选择时间' }]}
              >
                <SelectF
                  options={slotOptions}
                  modalProps={{
                    size: 'large',
                    disabled: !ready,
                    loading: reservationSlots.fetching,
                    placeholder,
                    notFoundContent: '暂无可约时间',
                    popupClassName: 'tuina-rsv-slot-dropdown',
                    style: { width: '100%' }
                  }}
                />
              </FormItem>
            )
          }}
        </FormItem>
        <FormItem
          label="最近问题"
          name="remark"
        >
          <TagSelectF options={TUINA_RECENT_ISSUE_OPTIONS} />
        </FormItem>
      </Form>
    )
  }
}

const mapStateToProps = (state) => ({
  memberList: state.tuina.memberList,
  memberEntitlements: state.tuina.memberEntitlements,
  therapistOnDuty: state.tuina.therapistOnDuty,
  reservationSlots: state.tuina.reservationSlots
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(ReservationCreateForm)
